import { createIntegration, PaymentFieldType, MethodType, RedirectMethod, RedirectType } from '@endeavour/payment-integration';
import { getWithdrawalForm } from './utils/get-withdrawal-form.js';
import { PaymentField, WithdrawalPixKeyType } from './method.interface.js';
import { z } from 'zod';

const INTEGRATION_ID = 'pay4fun';
const createPay4FunIntegration = ({ processPayment, getBankList, }) => {
    let sessionData = null;
    let getSession = null;
    const getSessionData = async () => {
        if (!getSession) {
            throw new Error('[Pay4Fun] No session provider');
        }
        if (!sessionData) {
            sessionData = await getSession(INTEGRATION_ID);
        }
        return sessionData;
    };
    return createIntegration({
        name: INTEGRATION_ID,
        async initialize(getIntegrationSession) {
            getSession = getIntegrationSession;
        },
        async destroy() {
            sessionData = null;
            getSession = null;
        },
        async getMethods({ methods }) {
            return methods.filter((method) => method.integrationId === INTEGRATION_ID);
        },
        async getSavedAccounts() {
            return [];
        },
        async getMethodDetailsForm({ type }) {
            const { sessionId, providerData: { email }, } = await getSessionData();
            const emailField = {
                type: PaymentFieldType.TEXT,
                name: PaymentField.EMAIL,
                key: PaymentField.EMAIL,
                defaultValue: email ? email : undefined,
            };
            const emailSchema = z.object({
                [PaymentField.EMAIL]: z.string().min(1, 'email.error.required').optional(),
            });
            if (type === MethodType.WITHDRAWAL) {
                const bankList = await getBankList({
                    sessionContextId: sessionId,
                });
                const fields = getWithdrawalForm(bankList);
                return {
                    fields: [...fields, emailField],
                    schema: z.intersection(z.union([
                        z.object({
                            [PaymentField.PIX_KEY_TYPE]: z.union([
                                z.literal(WithdrawalPixKeyType.CPF),
                                z.literal(WithdrawalPixKeyType.EMAIL),
                                z.literal(WithdrawalPixKeyType.TELEPHONE),
                            ]),
                        }),
                        z.object({
                            [PaymentField.BANK_CODE]: z
                                .string()
                                .min(1, 'bank-code.error.required')
                                .optional(),
                            [PaymentField.BANK_ACCOUNT]: z
                                .string()
                                .min(1, 'branch.error.required')
                                .optional(),
                            [PaymentField.BANK_ACCOUNT_TYPE]: z
                                .string()
                                .min(1, 'branch.error.required')
                                .optional(),
                            [PaymentField.BANK_BRANCH]: z
                                .string()
                                .min(1, 'branch.error.required')
                                .optional(),
                            [PaymentField.PIX_KEY_TYPE]: z
                                .string()
                                .min(1, 'payment-method.error.required')
                                .optional(),
                        }),
                    ]), emailSchema),
                };
            }
            return {
                fields: [emailField],
                schema: emailSchema,
            };
        },
        async processPayment({ providerId, type, formData }) {
            const { sessionId } = await getSessionData();
            if (!formData?.amount) {
                return {
                    success: false,
                    error: 'ERROR_AMOUNT_NOT_SPECIFIED',
                };
            }
            const isDeposit = type === MethodType.DEPOSIT;
            if (!providerId) {
                return {
                    success: false,
                    error: 'ERROR_PAYMENT_METHOD_NOT_SPECIFIED',
                };
            }
            try {
                const { sessionContextId, cashierLaunchUrl } = await processPayment({
                    type: isDeposit ? 'Deposit' : 'Withdrawal',
                    amount: formData.amount,
                    sessionContextId: sessionId,
                    externalPaymentMethodId: providerId,
                    email: formData.email ?? '',
                    // TODO: Backend states this field is required but its only for withdrawals hence the empty string.
                    pixKeyType: isDeposit ? '' : formData.pixKeyType ?? WithdrawalPixKeyType.CPF,
                    bankAccount: formData.bankAccount,
                    bankAccountType: formData.bankAccountType,
                    bankBranch: formData.bankBranch,
                    bankCode: formData.bankCode,
                });
                return {
                    success: true,
                    transactionId: sessionContextId ?? sessionId,
                    redirectOutput: cashierLaunchUrl
                        ? {
                            url: cashierLaunchUrl,
                            method: RedirectMethod.GET,
                            container: RedirectType.REDIRECT,
                            parameters: {},
                            width: 0,
                            height: 0,
                        }
                        : undefined,
                };
            }
            catch (e) {
                console.error(e);
                return {
                    success: false,
                    error: 'PAY4FUN_PROCESS_ERROR',
                    transactionId: sessionId,
                };
            }
        },
        getPendingWithdrawals() { },
        async cancelWithdrawal() {
            return false;
        },
    });
};

export { INTEGRATION_ID, createPay4FunIntegration };
